/**
 * Shared CAST-TIMING readiness math (#797) — the ONE client-side answer to
 * "which tick does this slot's TIMING gate open?".
 *
 * WHY IT EXISTS: two consumers need the same comparison and must never drift:
 *   • `ActionBar`'s cooldown sweep / GCD overlay — "is this slot dark right
 *     now, and how much of the sweep is left?"
 *   • `spellQueue.ts` (#797) — "the press is early; on WHICH tick does it
 *     become legal, so the queued cast can fire exactly then?"
 * Before #797 the comparisons lived only inside `computeActionBarState`
 * (`cd.readyAtTick > tick`, `me.gcdReadyAtTick > tick`). A second, private
 * copy inside the input layer could disagree with the sweep the player is
 * literally watching — that is a bug generator, so both call THIS.
 *
 * TIMING ONLY, ON PURPOSE. These helpers mirror the sim's three TIME-based
 * gates and nothing else. Resource (`cast.ts` Step 5b), school-lock (Step 5c),
 * range/LoS/positional (Steps 6+) and target validity are deliberately absent:
 * #797's Q3 locked "the client predicts timing only; the sim's own gates stay
 * authoritative". `castability.ts` owns the (separate, feedback-only) resource
 * prediction.
 *
 * MIRRORS THE SIM'S GATE ORDER AND ITS OFF-BY-ONE:
 *   • cast track (`cast.ts` Step 4, `castState.kind !== "idle"`): inputs are
 *     applied in `step.ts` phase (2), which runs BEFORE `advanceCasts` in
 *     phase (3). So on the very tick a cast RESOLVES (`endTick`, the wire's
 *     mirror of `resolveAtTick` / a channel's `endsAtTick`) the caster is
 *     still `casting` when the input lands ⇒ the track opens at `endTick + 1`,
 *     NOT `endTick`. Getting this wrong by one tick is the difference between
 *     a queued cast firing and being dropped.
 *   • cooldown / charges (Step 5): a charged ability gates on an available
 *     charge (`available <= 0` ⇒ wait for `nextChargeReadyAtTick`), everything
 *     else on `readyAtTick`. The `max` of the two is exact for both shapes —
 *     crossValidate pins `cooldownTicks === 0` for a charged ability, so its
 *     `readyAtTick` never sits ahead of `tick`.
 *   • GCD (Step 5, `tick < gcdReadyAt` ⇒ `onGcd`).
 *
 * Every function is pure and integer-only (wire ticks in, wire ticks out) —
 * no clock, no store, no DOM.
 */

import type { WireSimState } from "@pit/protocol";

/** A wire entity as the HUD/input layers see it (`WireSimState.entities[n]`). */
export type ReadinessEntity = WireSimState["entities"][number];

/** One wire cooldown entry (`ReadinessEntity.cooldowns[n]`). */
export type ReadinessCooldown = ReadinessEntity["cooldowns"][number];

/**
 * The tick this slot's COOLDOWN/CHARGE gate opens. `0` (always open) for a
 * slot with no cooldown entry at all.
 *
 * `max(readyAtTick, chargeGate)` covers both ability shapes with one
 * expression — see the module doc. A charge-based slot with a charge in hand
 * is gated only by `readyAtTick` (0 by content invariant), and one with zero
 * charges waits for the next refill.
 */
export function cooldownGateTick(cd: ReadinessCooldown | undefined): number {
  if (cd === undefined) return 0;
  const chargeGate =
    cd.charges === null || cd.charges.available > 0 ? 0 : cd.charges.nextChargeReadyAtTick;
  return Math.max(cd.readyAtTick, chargeGate);
}

/** The tick the GCD gate opens (`cast.ts`: `tick < gcdReadyAt` ⇒ `onGcd`). */
export function gcdGateTick(me: ReadinessEntity): number {
  return me.gcdReadyAtTick;
}

/**
 * The tick the MAIN CAST TRACK frees up — `0` when idle, else `endTick + 1`.
 * The `+ 1` is load-bearing: see the module doc's step-ordering note.
 *
 * Slot-agnostic by design: `cast.ts` Step 4 rejects ANY non-`offCast` cast
 * while the track is busy, whichever slot is casting. (An `offCast` ability
 * (#86) is exempt sim-side; the client does not model that exemption — the
 * worst case is a queued off-cast firing one tick later than it strictly
 * had to, never an illegal one.)
 */
export function castTrackGateTick(me: ReadinessEntity): number {
  return me.cast === null ? 0 : me.cast.endTick + 1;
}

/**
 * The earliest tick at which `slot` passes EVERY timing gate — the max of the
 * three above. A value `<= snapshot.tick` means "legal right now".
 */
export function castReadyTick(me: ReadinessEntity, slot: number): number {
  return Math.max(
    castTrackGateTick(me),
    cooldownGateTick(me.cooldowns.find((c) => c.slot === slot)),
    gcdGateTick(me),
  );
}
